import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/admin/layout/AdminLayout';
import { EditProviderDialog } from '@/components/admin/peo/verifications/EditProviderDialog';
import { ContactUserDropdown } from '@/components/admin/peo/verifications/ContactUserDropdown';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import { CheckCircle, XCircle, Clock, User, Building, AlertTriangle, Shield, AlertCircle, FileText, ExternalLink } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
        
interface PEOVerificationItem {
  id: string;
  user_id: string;
  verification_status: 'pending' | 'verified' | 'rejected';
  provider_id?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at?: string;
  user_profile?: {
    first_name?: string;
    last_name?: string;
    email: string;
    organization_name?: string;
  };
  provider?: {
    id: string;
    name: string;
  };
}

interface PEOProvider {
  id: string;
  name: string;
}

interface PEOVendorApplication {
  id: string;
  company_name: string;
  website_url?: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  states_served: string[];
  employee_range?: string;
  notes?: string;
  certifications: any;
  status: 'submitted' | 'approved' | 'rejected' | 'converted';
  source?: string;
  referral_code?: string;
  user_id?: string;
  created_at: string;
  updated_at: string;
}

const PEOVerifications = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isSuperAdmin, isLoading: adminLoading, error: adminError } = useAdminStatus();
  const [selectedStatus, setSelectedStatus] = useState<string>('pending');
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('representatives');
  const [selectedAppStatus, setSelectedAppStatus] = useState<string>('submitted');

  // Fetch PEO verification requests
  const { data: verifications, isLoading: verificationsLoading } = useQuery({
    queryKey: ['peo-verifications'],
    queryFn: async () => {
      // Get all users with user_type = 'peo' (these are the ones that need verification)
      const { data: peoUsers, error: peoError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, organization_name, created_at')
        .eq('user_type', 'peo')
        .order('created_at', { ascending: false });

      if (peoError) {
        console.error('Error fetching PEO users:', peoError);
        throw peoError;
      }

      // Get existing peo_representatives records
      const { data: repsData, error: repsError } = await supabase
        .from('peo_representatives')
        .select(`
          id,
          user_id,
          verification_status,
          provider_id,
          rejection_reason,
          verified_by,
          verified_at,
          created_at,
          updated_at
        `);

      if (repsError) {
        console.error('Error fetching peo representatives:', repsError);
        throw repsError;
      }

      // Get provider data for existing representatives
      const providerIds = repsData?.filter(rep => rep.provider_id).map(rep => rep.provider_id) || [];
      let providersData: any[] = [];
      
      if (providerIds.length > 0) {
        const { data, error: providersError } = await supabase
          .from('peo_providers')
          .select('id, name')
          .in('id', providerIds);

        if (providersError) {
          console.error('Error fetching providers:', providersError);
        } else {
          providersData = data || [];
        }
      }

      // Create verification items
      const verificationItems: PEOVerificationItem[] = [];

      // Add users who are in peo_representatives table
      repsData?.forEach(rep => {
        const profile = peoUsers?.find(p => p.id === rep.user_id);
        const provider = providersData.find(p => p.id === rep.provider_id);

        if (profile) {
          verificationItems.push({
            id: rep.id,
            user_id: rep.user_id,
            verification_status: rep.verification_status as 'pending' | 'verified' | 'rejected',
            provider_id: rep.provider_id,
            rejection_reason: rep.rejection_reason,
            created_at: rep.created_at,
            updated_at: rep.updated_at,
            user_profile: {
              first_name: profile.first_name,
              last_name: profile.last_name,
              email: profile.email,
              organization_name: profile.organization_name
            },
            provider: provider ? {
              id: provider.id,
              name: provider.name
            } : undefined
          });
        }
      });

      // Add users who are NOT in peo_representatives table (these are truly pending)
      const existingUserIds = repsData?.map(rep => rep.user_id) || [];
      const pendingUsers = peoUsers?.filter(user => !existingUserIds.includes(user.id)) || [];

      pendingUsers.forEach(user => {
        verificationItems.push({
          id: `pending-${user.id}`, // Temporary ID for pending users
          user_id: user.id,
          verification_status: 'pending',
          provider_id: undefined,
          rejection_reason: null,
          created_at: user.created_at,
          updated_at: undefined,
          user_profile: {
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            organization_name: user.organization_name
          },
          provider: undefined
        });
      });

      // Sort by created_at (most recent first)
      verificationItems.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      return verificationItems;
    }
  });

  // Fetch PEO vendor applications
  const { data: vendorApplications, isLoading: applicationsLoading } = useQuery({
    queryKey: ['peo-vendor-applications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('peo_vendor_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching vendor applications:', error);
        throw error;
      }

      return data as PEOVendorApplication[];
    }
  });

  // Fetch PEO providers for assignment
  const { data: providers } = useQuery({
    queryKey: ['peo-providers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('peo_providers')
        .select('id, name')
        .order('name');

      if (error) throw error;
      return data as PEOProvider[];
    }
  });

  // Approve verification mutation using RPC function
  const approveVerification = useMutation({
    mutationFn: async ({ verificationId, providerId, userId }: { verificationId: string; providerId: string; userId?: string }) => {
      console.log('Approving verification:', { verificationId, providerId, userId });
      
      // Check if this is a pending user (verificationId starts with 'pending-')
      const isPendingUser = verificationId.startsWith('pending-');
      
      if (isPendingUser && userId) {
        // Use RPC function for new users
        const { data, error } = await supabase.rpc('link_user_to_provider', {
          p_user_id: userId,
          p_provider_id: providerId,
          p_verification_status: 'verified'
        });

        console.log('RPC link_user_to_provider result:', { data, error });
        
        if (error) {
          console.error('RPC link_user_to_provider error:', error);
          throw new Error(`Failed to link user to provider: ${error.message}`);
        }

        if (!data) {
          throw new Error('Failed to create verification link');
        }
      } else {
        // For existing records, still use direct update
        await supabase.auth.refreshSession();
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user?.id) {
          throw new Error('Authentication required. Please refresh the page and try again.');
        }

        const { error } = await supabase
          .from('peo_representatives')
          .update({
            verification_status: 'verified',
            provider_id: providerId,
            verified_by: session.user.id,
            verified_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', verificationId);

        if (error) {
          console.error('Error updating existing verification record:', error);
          throw new Error(`Failed to update verification: ${error.message}`);
        }
      }
    },
    onSuccess: () => {
      // Invalidate all variations of the query to ensure UI updates
      queryClient.invalidateQueries({ queryKey: ['peo-verifications'] });
      queryClient.invalidateQueries({ queryKey: ['peo-providers'] });
      toast({
        title: "Verification Approved",
        description: "PEO representative has been verified successfully."
      });
      setSelectedProviderId('');
    },
    onError: (error: any) => {
      console.error('Approval error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to approve verification. Please try again.",
        variant: "destructive"
      });
    }
  });

  // Reject verification mutation
  const rejectVerification = useMutation({
    mutationFn: async ({ verificationId, reason, userId }: { verificationId: string; reason: string; userId?: string }) => {
      // Force session refresh before operation
      await supabase.auth.refreshSession();
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user?.id) {
        throw new Error('Authentication required. Please refresh the page and try again.');
      }
      
      // Check if this is a pending user (verificationId starts with 'pending-')
      const isPendingUser = verificationId.startsWith('pending-');
      
      if (isPendingUser && userId) {
        console.log('Inserting new rejection record for pending user:', userId);
        // Insert new record for pending user with null provider_id (allowed by RLS/policy)
        const { data, error } = await supabase
          .from('peo_representatives')
          .insert({
            user_id: userId,
            provider_id: null,
            verification_status: 'rejected',
            rejection_reason: reason
          })
          .select(); // May return empty if SELECT RLS disallows returning rows

        console.log('Insert result (reject):', { dataLength: data?.length, error });
        
        if (error) {
          console.error('Error inserting new rejection record:', error);
          throw new Error(`Failed to create rejection: ${error.message}. Details: ${JSON.stringify(error)}`);
        }

        console.log('Successfully processed rejection insert.');
      } else {
        console.log('Updating existing record for rejection:', verificationId);
        // Update existing record
        const { data, error } = await supabase
          .from('peo_representatives')
          .update({
            verification_status: 'rejected',
            rejection_reason: reason,
            updated_at: new Date().toISOString()
          })
          .eq('id', verificationId)
          .select(); // May return empty if SELECT RLS disallows returning rows

        console.log('Update result (reject):', { dataLength: data?.length, error });
        
        if (error) {
          console.error('Error updating existing record for rejection:', error);
          throw new Error(`Failed to update rejection: ${error.message}. Details: ${JSON.stringify(error)}`);
        }

        console.log('Successfully processed rejection update.');
      }
    },
    onSuccess: () => {
      // Invalidate all variations of the query to ensure UI updates
      queryClient.invalidateQueries({ queryKey: ['peo-verifications'] });
      queryClient.invalidateQueries({ queryKey: ['peo-providers'] });
      toast({
        title: "Verification Rejected",
        description: "PEO representative verification has been rejected."
      });
      setRejectionReason('');
    },
    onError: (error: any) => {
      console.error('Rejection error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reject verification. Please try again.",
        variant: "destructive"
      });
    }
  });

  // Unlink verification (for verified users -> mark as rejected and clear provider)
  const unlinkVerification = useMutation({
    mutationFn: async ({ verificationId }: { verificationId: string }) => {
      await supabase.auth.refreshSession();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('Authentication required. Please refresh the page and try again.');
      }

      const { error } = await supabase
        .from('peo_representatives')
        .update({
          verification_status: 'rejected',
          provider_id: null,
          rejection_reason: 'Unlinked by admin',
          updated_at: new Date().toISOString()
        })
        .eq('id', verificationId);

      if (error) {
        throw new Error(`Failed to unlink: ${error.message}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peo-verifications'] });
      queryClient.invalidateQueries({ queryKey: ['peo-providers'] });
      toast({ title: 'Representative unlinked', description: 'The user is now marked as rejected.' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to unlink representative.', variant: 'destructive' });
    }
  });

  // Vendor application mutations
  const updateApplicationStatus = useMutation({
    mutationFn: async ({ applicationId, status, reason }: { applicationId: string; status: string; reason?: string }) => {
      const updateData: any = { status, updated_at: new Date().toISOString() };
      if (reason) updateData.rejection_reason = reason;
      
      const { error } = await supabase
        .from('peo_vendor_applications')
        .update(updateData)
        .eq('id', applicationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peo-vendor-applications'] });
      toast({
        title: "Status Updated",
        description: "Application status has been updated successfully."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update application status.",
        variant: "destructive"
      });
    }
  });

  const createProviderFromApplication = useMutation({
    mutationFn: async ({ applicationId }: { applicationId: string }) => {
      const application = vendorApplications?.find(app => app.id === applicationId);
      if (!application) throw new Error('Application not found');

      // Create PEO provider
      const { data: provider, error: providerError } = await supabase
        .from('peo_providers')
        .insert({
          name: application.company_name,
          contact_email: application.contact_email,
          employee_range: application.employee_range,
          states_served: application.states_served,
          specialties: [], // Default empty array
          rating: 0, // Default rating
          years_in_business: 1, // Default value
          is_esac_accredited: application.certifications?.esacMember || false,
          napeo_member: application.certifications?.napeoMember || false,
          website: application.website_url,
          notes_internal: `Created from application ID: ${applicationId}. ${application.notes || ''}`
        })
        .select()
        .single();

      if (providerError) throw providerError;

      // Update application status to converted
      await updateApplicationStatus.mutateAsync({ applicationId, status: 'converted' });

      return provider;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peo-vendor-applications'] });
      queryClient.invalidateQueries({ queryKey: ['peo-providers'] });
      toast({
        title: "Provider Created",
        description: "Successfully created PEO provider from application."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create provider from application.",
        variant: "destructive"
      });
    }
  });

  const getApplicationStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      case 'converted':
        return <Badge variant="outline" className="bg-blue-50 text-blue-800"><Building className="h-3 w-3 mr-1" />Converted</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Submitted</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'verified':
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  const pendingCount = verifications?.filter(v => v.verification_status === 'pending').length || 0;
  const verifiedCount = verifications?.filter(v => v.verification_status === 'verified').length || 0;
  const rejectedCount = verifications?.filter(v => v.verification_status === 'rejected').length || 0;

  // Application stats
  const submittedAppCount = vendorApplications?.filter(app => app.status === 'submitted').length || 0;
  const approvedAppCount = vendorApplications?.filter(app => app.status === 'approved').length || 0;
  const convertedAppCount = vendorApplications?.filter(app => app.status === 'converted').length || 0;

  const filteredVerifications = useMemo(() => {
    if (!verifications) return [] as PEOVerificationItem[];
    if (selectedStatus === 'all') return verifications;
    return verifications.filter(v => v.verification_status === selectedStatus);
  }, [verifications, selectedStatus]);

  const filteredApplications = useMemo(() => {
    if (!vendorApplications) return [] as PEOVendorApplication[];
    if (selectedAppStatus === 'all') return vendorApplications;
    return vendorApplications.filter(app => app.status === selectedAppStatus);
  }, [vendorApplications, selectedAppStatus]);

  if ((verificationsLoading || applicationsLoading) || adminLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2">Loading verification data...</span>
        </div>
      </AdminLayout>
    );
  }

  // Show admin access error if user is not authorized
  if (!isSuperAdmin) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Shield className="h-16 w-16 text-muted-foreground" />
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">Super admin privileges required to manage PEO verifications.</p>
            {adminError && (
              <p className="text-sm text-destructive mt-2">Error: {adminError}</p>
            )}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">PEO Management</h1>
            <p className="text-muted-foreground">Manage PEO provider representatives and applications</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Reps</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingCount}</div>
              <p className="text-xs text-muted-foreground">Awaiting review</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Verified Reps</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{verifiedCount}</div>
              <p className="text-xs text-muted-foreground">Successfully verified</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">New Applications</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{submittedAppCount}</div>
              <p className="text-xs text-muted-foreground">Pending review</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Converted</CardTitle>
              <Building className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{convertedAppCount}</div>
              <p className="text-xs text-muted-foreground">Apps to providers</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="representatives">PEO Representatives</TabsTrigger>
            <TabsTrigger value="applications">Provider Applications</TabsTrigger>
          </TabsList>
          
          <TabsContent value="representatives" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Manage PEO provider representative verification requests</p>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Representatives Table */}
            <Card>
              <CardHeader>
                <CardTitle>Representative Verification Requests</CardTitle>
                <CardDescription>Review and manage PEO representative verification requests</CardDescription>
              </CardHeader>
              <CardContent>
            {!verifications || filteredVerifications.length === 0 ? (
              <div className="text-center py-8">
                <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No verification requests found</p>
              </div>
            ) : (
              <div className="overflow-x-auto"><Table className="min-w-[780px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>PEO Provider</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead className="w-56">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVerifications.map((verification) => (
                    <TableRow key={verification.id}>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {verification.user_profile?.first_name || 'Unknown'} {verification.user_profile?.last_name || 'User'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {verification.user_profile?.organization_name || 'Organization not specified'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {verification.user_profile?.email || 'Email not available'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {verification.provider ? (
                          <div className="flex items-center space-x-2">
                            <Building className="h-4 w-4 text-muted-foreground" />
                            <span>{verification.provider.name}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Not assigned</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(verification.verification_status)}</TableCell>
                      <TableCell>
                        {new Date(verification.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="min-w-[220px]">
                        <div className="flex flex-wrap gap-2">
                          {verification.verification_status === 'pending' && (
                            <>
                              {/* Show contact options for incomplete profiles */}
                              {(!verification.user_profile?.first_name || 
                                !verification.user_profile?.last_name || 
                                !verification.user_profile?.organization_name) && (
                                <ContactUserDropdown
                                  verification={{
                                    id: verification.id,
                                    user_id: verification.user_id,
                                    email: verification.user_profile?.email || null,
                                    first_name: verification.user_profile?.first_name || null,
                                    last_name: verification.user_profile?.last_name || null,
                                    organization_name: verification.user_profile?.organization_name || null
                                  }}
                                />
                              )}
                              
                              {/* Approve Dialog */}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="default">
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Approve
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Approve Verification</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Select the PEO provider to associate with this representative.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <div className="py-4">
                                    <Select value={selectedProviderId} onValueChange={setSelectedProviderId}>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select PEO Provider" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {providers?.map((provider) => (
                                          <SelectItem key={provider.id} value={provider.id}>
                                            {provider.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel onClick={() => setSelectedProviderId('')}>
                                      Cancel
                                    </AlertDialogCancel>
                                     <AlertDialogAction
                                       onClick={() => {
                                         if (selectedProviderId) {
                                           approveVerification.mutate({
                                             verificationId: verification.id,
                                             providerId: selectedProviderId,
                                             userId: verification.user_id
                                           });
                                         }
                                       }}
                                        disabled={!selectedProviderId || approveVerification.isPending}
                                      >
                                        {approveVerification.isPending ? "Approving..." : "Approve"}
                                      </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>

                              {/* Reject Dialog */}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="destructive">
                                    <XCircle className="h-4 w-4 mr-1" />
                                    Reject
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Reject Verification</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Please provide a reason for rejecting this verification request.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <div className="py-4">
                                    <Textarea
                                      placeholder="Enter rejection reason..."
                                      value={rejectionReason}
                                      onChange={(e) => setRejectionReason(e.target.value)}
                                    />
                                  </div>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel onClick={() => setRejectionReason('')}>
                                      Cancel
                                    </AlertDialogCancel>
                                     <AlertDialogAction
                                       onClick={() => {
                                         if (rejectionReason.trim()) {
                                           rejectVerification.mutate({
                                             verificationId: verification.id,
                                             reason: rejectionReason,
                                             userId: verification.user_id
                                           });
                                         }
                                       }}
                                        disabled={!rejectionReason.trim() || rejectVerification.isPending}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        {rejectVerification.isPending ? "Rejecting..." : "Reject"}
                                      </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}

                          {verification.verification_status === 'verified' && (
                              <>
                                <EditProviderDialog
                                  verification={{
                                    id: verification.id,
                                    user_id: verification.user_id,
                                    verification_status: verification.verification_status,
                                    provider: verification.provider
                                  }}
                                  providers={providers || []}
                                  onSuccess={() => {
                                    queryClient.invalidateQueries({ queryKey: ['peo-verifications'] });
                                    queryClient.invalidateQueries({ queryKey: ['peo-providers'] });
                                  }}
                                />
                                {/* Unlink instead of Delete: marks as rejected and clears provider */}
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="sm" variant="destructive">
                                      <XCircle className="h-4 w-4 mr-1" />
                                      Unlink
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Unlink Representative</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will remove the provider association and mark the user as rejected. Continue?
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => unlinkVerification.mutate({ verificationId: verification.id })}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        disabled={unlinkVerification.isPending}
                                      >
                                        {unlinkVerification.isPending ? 'Unlinking...' : 'Unlink'}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
        
                          {verification.verification_status === 'rejected' && verification.rejection_reason && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline">
                                  <AlertTriangle className="h-4 w-4 mr-1" />
                                  View Reason
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Rejection Reason</AlertDialogTitle>
                                </AlertDialogHeader>
                                <div className="py-4">
                                  <p className="text-sm text-muted-foreground">{verification.rejection_reason}</p>
                                </div>
                                <AlertDialogFooter>
                                  <AlertDialogAction>Close</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="applications" className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Review and manage PEO provider applications</p>
          <Select value={selectedAppStatus} onValueChange={setSelectedAppStatus}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Applications Table */}
        <Card>
          <CardHeader>
            <CardTitle>PEO Provider Applications</CardTitle>
            <CardDescription>Review applications from companies wanting to join the PEO network</CardDescription>
          </CardHeader>
          <CardContent>
            {!vendorApplications || filteredApplications.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No applications found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="min-w-[1000px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>States Served</TableHead>
                      <TableHead>Employee Range</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="w-64">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredApplications.map((application) => (
                      <TableRow key={application.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <Building className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{application.company_name}</span>
                            </div>
                            {application.website_url && (
                              <a 
                                href={application.website_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline flex items-center"
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                Website
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-medium">{application.contact_name}</p>
                            <p className="text-sm text-muted-foreground">{application.contact_email}</p>
                            {application.contact_phone && (
                              <p className="text-sm text-muted-foreground">{application.contact_phone}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {application.states_served?.slice(0, 3).join(', ')}
                            {application.states_served?.length > 3 && ` +${application.states_served.length - 3} more`}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{application.employee_range || 'Not specified'}</span>
                        </TableCell>
                        <TableCell>{getApplicationStatusBadge(application.status)}</TableCell>
                        <TableCell>
                          {new Date(application.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            {application.status === 'submitted' && (
                              <>
                                {/* View Details Dialog */}
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="sm" variant="outline">
                                      <FileText className="h-4 w-4 mr-1" />
                                      View Details
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Application Details - {application.company_name}</AlertDialogTitle>
                                    </AlertDialogHeader>
                                    <div className="space-y-4">
                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <h4 className="font-medium mb-2">Company Information</h4>
                                          <div className="space-y-2 text-sm">
                                            <p><strong>Name:</strong> {application.company_name}</p>
                                            {application.website_url && <p><strong>Website:</strong> {application.website_url}</p>}
                                          </div>
                                        </div>
                                        <div>
                                          <h4 className="font-medium mb-2">Contact Information</h4>
                                          <div className="space-y-2 text-sm">
                                            <p><strong>Name:</strong> {application.contact_name}</p>
                                            <p><strong>Email:</strong> {application.contact_email}</p>
                                            {application.contact_phone && <p><strong>Phone:</strong> {application.contact_phone}</p>}
                                          </div>
                                        </div>
                                      </div>
                                      
                                      <div>
                                        <h4 className="font-medium mb-2">Business Details</h4>
                                        <div className="space-y-2 text-sm">
                                          <p><strong>States Served:</strong> {application.states_served?.join(', ') || 'Not specified'}</p>
                                          <p><strong>Employee Range:</strong> {application.employee_range || 'Not specified'}</p>
                                        </div>
                                      </div>
                                      
                                      {application.certifications && Object.values(application.certifications).some(Boolean) && (
                                        <div>
                                          <h4 className="font-medium mb-2">Certifications</h4>
                                          <div className="space-y-1 text-sm">
                                            {application.certifications.esacMember && <p>• ESAC Member</p>}
                                            {application.certifications.irs5500Member && <p>• IRS 5500 Member</p>}
                                            {application.certifications.napeoMember && <p>• NAPEO Member</p>}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {application.notes && (
                                        <div>
                                          <h4 className="font-medium mb-2">Additional Notes</h4>
                                          <p className="text-sm text-muted-foreground">{application.notes}</p>
                                        </div>
                                      )}
                                    </div>
                                    <AlertDialogFooter>
                                      <AlertDialogAction>Close</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>

                                {/* Approve Button */}
                                <Button 
                                  size="sm" 
                                  variant="default" 
                                  onClick={() => updateApplicationStatus.mutate({ 
                                    applicationId: application.id, 
                                    status: 'approved' 
                                  })}
                                  disabled={updateApplicationStatus.isPending}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve
                                </Button>

                                {/* Create Provider Button */}
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => createProviderFromApplication.mutate({ 
                                    applicationId: application.id 
                                  })}
                                  disabled={createProviderFromApplication.isPending}
                                >
                                  <Building className="h-4 w-4 mr-1" />
                                  Create Provider
                                </Button>

                                {/* Reject Dialog */}
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="sm" variant="destructive">
                                      <XCircle className="h-4 w-4 mr-1" />
                                      Reject
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Reject Application</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Please provide a reason for rejecting this application.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <div className="py-4">
                                      <Textarea
                                        placeholder="Enter rejection reason..."
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                      />
                                    </div>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel onClick={() => setRejectionReason('')}>
                                        Cancel
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => {
                                          if (rejectionReason.trim()) {
                                            updateApplicationStatus.mutate({
                                              applicationId: application.id,
                                              status: 'rejected',
                                              reason: rejectionReason
                                            });
                                          }
                                        }}
                                        disabled={!rejectionReason.trim() || updateApplicationStatus.isPending}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        {updateApplicationStatus.isPending ? "Rejecting..." : "Reject"}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}

                            {application.status !== 'submitted' && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="outline">
                                    <FileText className="h-4 w-4 mr-1" />
                                    View Details
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Application Details - {application.company_name}</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Status: {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <h4 className="font-medium mb-2">Company Information</h4>
                                        <div className="space-y-2 text-sm">
                                          <p><strong>Name:</strong> {application.company_name}</p>
                                          {application.website_url && <p><strong>Website:</strong> {application.website_url}</p>}
                                        </div>
                                      </div>
                                      <div>
                                        <h4 className="font-medium mb-2">Contact Information</h4>
                                        <div className="space-y-2 text-sm">
                                          <p><strong>Name:</strong> {application.contact_name}</p>
                                          <p><strong>Email:</strong> {application.contact_email}</p>
                                          {application.contact_phone && <p><strong>Phone:</strong> {application.contact_phone}</p>}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div>
                                      <h4 className="font-medium mb-2">Business Details</h4>
                                      <div className="space-y-2 text-sm">
                                        <p><strong>States Served:</strong> {application.states_served?.join(', ') || 'Not specified'}</p>
                                        <p><strong>Employee Range:</strong> {application.employee_range || 'Not specified'}</p>
                                      </div>
                                    </div>
                                    
                                    {application.notes && (
                                      <div>
                                        <h4 className="font-medium mb-2">Additional Notes</h4>
                                        <p className="text-sm text-muted-foreground">{application.notes}</p>
                                      </div>
                                    )}
                                  </div>
                                  <AlertDialogFooter>
                                    <AlertDialogAction>Close</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  </div>
</AdminLayout>
  );
};

export default PEOVerifications;
